import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

import { useModalContext } from "@/contexts/modal-context";

const MEDIA_STATUS_PARAM = "status_id";
const MEDIA_ATTACHMENT_PARAM = "attachment_id";

/**
 * Mirrors the currently visible attachment into the URL.
 *
 * Uses `replace` so swiping does not push one history entry per attachment.
 * The early-return guard below is what keeps this from looping on its own
 * writes despite `searchParams` being a dependency.
 */
function useSyncMediaUrl(statusId?: string, attachmentId?: string) {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!statusId || !attachmentId) return;
    if (searchParams.get(MEDIA_ATTACHMENT_PARAM) === attachmentId) return;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set(MEDIA_STATUS_PARAM, statusId);
    nextParams.set(MEDIA_ATTACHMENT_PARAM, attachmentId);

    setSearchParams(nextParams, { replace: true });
  }, [statusId, attachmentId, searchParams, setSearchParams]);
}

/** Drops the params, for closing the lightbox or recovering from a bad deep link. */
function useClearMediaParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  return useCallback(() => {
    if (!searchParams.has(MEDIA_STATUS_PARAM)) return;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete(MEDIA_STATUS_PARAM);
    nextParams.delete(MEDIA_ATTACHMENT_PARAM);

    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);
}

/**
 * Opens the lightbox for a deep-linked attachment.
 *
 * The ref matters: `useSyncMediaUrl` writes these same params whenever the
 * lightbox is open, so reading them live would feed our own writes back in and
 * re-open a modal the user opened by clicking.
 */
function useMediaDeepLink() {
  const [searchParams] = useSearchParams();
  const { openModal } = useModalContext();

  const initialParams = useRef({
    statusId: searchParams.get(MEDIA_STATUS_PARAM),
    attachmentId: searchParams.get(MEDIA_ATTACHMENT_PARAM) ?? undefined,
  }).current;

  useEffect(() => {
    if (!initialParams.statusId) return;

    openModal("MEDIA", {
      statusId: initialParams.statusId,
      attachmentId: initialParams.attachmentId,
    });
  }, []);
}

export {
  useMediaDeepLink,
  useSyncMediaUrl,
  useClearMediaParams,
  MEDIA_STATUS_PARAM,
  MEDIA_ATTACHMENT_PARAM,
};
